import React, { useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { ArrowLeft, Download, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { useAuth } from "@/contexts/AuthContext";
import { LOGOS, BRANCHES } from "@/lib/brand";

const GROUPE_LOGO =
  "https://customer-assets.emergentagent.com/job_enterprise-hub-357/artifacts/6sv95ykm_Equinoxe_Groupe_noir-resize.png";

const COLORS = ["#8b5cf6", "#ea580c", "#0ea5e9"];

/* ===== TEMPLATES ===== */

function ColorStripe({ height = 12 }) {
  return (
    <div className="flex w-full" style={{ height }}>
      {COLORS.map((c, i) => (
        <div key={i} className="flex-1" style={{ backgroundColor: c }} />
      ))}
    </div>
  );
}

/* ====== POST GROUPE (1080×1080) ====== */
function PostGroupe() {
  return (
    <div
      className="relative bg-black text-white overflow-hidden flex flex-col"
      style={{ width: 1080, height: 1080 }}
    >
      <ColorStripe height={26} />
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(60% 50% at 50% 50%, rgba(255,255,255,0.05), transparent 70%)" }}
      />
      <div className="relative flex-1 flex flex-col items-center justify-center text-center px-16">
        <img src={GROUPE_LOGO} alt="" style={{ width: 360, height: 360, objectFit: "contain", marginTop: 30 }} />
        <div style={{ fontSize: 22, letterSpacing: "0.3em", color: "#9ca3af", textTransform: "uppercase", marginTop: 20 }}>
          Groupe Equinoxe
        </div>
        <h1
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 92,
            fontWeight: 700,
            letterSpacing: "-0.02em",
            lineHeight: 0.98,
            marginTop: 22,
          }}
        >
          Trois expertises.
          <br />
          <span style={{ color: "#9ca3af" }}>Une même exigence.</span>
        </h1>
        <div className="flex items-center justify-center mt-12" style={{ gap: 28, fontSize: 22, letterSpacing: "0.12em" }}>
          <span style={{ color: "#8b5cf6", fontWeight: 700 }}>EQUINOXE STUDIO</span>
          <span style={{ color: "#555" }}>·</span>
          <span style={{ color: "#ea580c", fontWeight: 700 }}>EQUINOXE SÉCURITÉ</span>
          <span style={{ color: "#555" }}>·</span>
          <span style={{ color: "#0ea5e9", fontWeight: 700 }}>EQUINOXE PROPRETÉ</span>
        </div>
      </div>
      <div className="relative px-16 pb-14 flex items-end justify-between">
        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 36, fontWeight: 700, letterSpacing: "0.04em", color: "#fff", textTransform: "uppercase" }}>
          groupeequinoxe.fr
        </div>
        <div style={{ fontSize: 18, color: "#9ca3af", letterSpacing: "0.22em", textTransform: "uppercase" }}>
          Devis sous 24h
        </div>
      </div>
      <ColorStripe height={26} />
    </div>
  );
}

/* ====== POST PÔLE (1080×1080) ====== */
function PostPole({ branch }) {
  const b = branch;
  const services = b.services.filter((s) => s !== "Autre").slice(0, 4);
  return (
    <div
      className="relative bg-black text-white overflow-hidden flex flex-col"
      style={{ width: 1080, height: 1080 }}
    >
      <div style={{ height: 36, backgroundColor: b.accent }} />
      <div
        className="absolute inset-0"
        style={{ background: `radial-gradient(70% 50% at 50% 0%, ${b.accent}33, transparent 70%)` }}
      />
      <div className="relative flex-1 flex flex-col items-center text-center px-16 pt-14">
        {/* Logo dans carré blanc */}
        <div
          className="rounded-md bg-white flex items-center justify-center"
          style={{ width: 300, height: 300, boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7)" }}
        >
          <img src={LOGOS[b.key]} alt={b.name} style={{ width: "92%", height: "92%", objectFit: "contain" }} />
        </div>

        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 36, fontWeight: 600, color: b.accent, marginTop: 40, lineHeight: 1 }}>
          Equinoxe
        </div>
        <div
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 96,
            fontWeight: 700,
            letterSpacing: "-0.02em",
            color: "#fff",
            lineHeight: 1,
            marginTop: 6,
          }}
        >
          {b.short}
        </div>
        <div style={{ color: b.accent, fontSize: 24, fontWeight: 500, marginTop: 24 }}>{b.tagline}</div>

        <ul style={{ listStyle: "none", padding: 0, margin: "44px 0 0 0", textAlign: "left", maxWidth: 720 }}>
          {services.map((s) => (
            <li key={s} style={{ fontSize: 26, color: "#e5e5e5", lineHeight: 1.3, marginBottom: 14, paddingLeft: 28, position: "relative", fontWeight: 500 }}>
              <span style={{ position: "absolute", left: 0, top: 12, width: 10, height: 10, backgroundColor: b.accent, borderRadius: 2 }} />
              {s}
            </li>
          ))}
        </ul>
      </div>
      <div className="relative px-16 pb-14 flex items-end justify-between">
        <div>
          <div style={{ fontSize: 16, color: "#9ca3af", letterSpacing: "0.25em", textTransform: "uppercase", fontWeight: 600 }}>
            Devis sous 24h
          </div>
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 44, fontWeight: 700, marginTop: 8 }}>
            05&nbsp;32&nbsp;18&nbsp;90&nbsp;44
          </div>
        </div>
        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 28, fontWeight: 700, letterSpacing: "0.04em", color: "#fff", textTransform: "uppercase" }}>
          groupeequinoxe.fr
        </div>
      </div>
      <div style={{ height: 24, backgroundColor: b.accent }} />
    </div>
  );
}

/* ====== STORY GROUPE (1080×1920) ====== */
function StoryGroupe() {
  return (
    <div
      className="relative bg-black text-white overflow-hidden flex flex-col"
      style={{ width: 1080, height: 1920 }}
    >
      <ColorStripe height={32} />
      <div className="relative flex-1 flex flex-col items-center justify-center text-center px-16">
        <img src={GROUPE_LOGO} alt="" style={{ width: 540, height: 540, objectFit: "contain" }} />
        <div style={{ fontSize: 26, letterSpacing: "0.3em", color: "#9ca3af", textTransform: "uppercase", marginTop: 40 }}>
          Groupe Equinoxe
        </div>
        <h1
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 112,
            fontWeight: 700,
            letterSpacing: "-0.02em",
            lineHeight: 0.98,
            marginTop: 32,
          }}
        >
          Trois<br />expertises.<br />
          <span style={{ color: "#9ca3af" }}>Une même<br />exigence.</span>
        </h1>
        <div className="flex flex-col items-center mt-16" style={{ gap: 12, fontSize: 30 }}>
          <span style={{ color: "#8b5cf6", fontWeight: 700, letterSpacing: "0.1em" }}>EQUINOXE STUDIO</span>
          <span style={{ color: "#ea580c", fontWeight: 700, letterSpacing: "0.1em" }}>EQUINOXE SÉCURITÉ</span>
          <span style={{ color: "#0ea5e9", fontWeight: 700, letterSpacing: "0.1em" }}>EQUINOXE PROPRETÉ</span>
        </div>
      </div>
      <div className="relative px-16 pb-20 text-center">
        <div style={{ fontSize: 22, color: "#9ca3af", letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 600 }}>
          Devis sous 24h
        </div>
        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 76, fontWeight: 700, marginTop: 14 }}>
          05&nbsp;32&nbsp;18&nbsp;90&nbsp;44
        </div>
        <div style={{ fontSize: 26, color: "#fff", letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 18, fontWeight: 600 }}>
          groupeequinoxe.fr
        </div>
      </div>
      <ColorStripe height={32} />
    </div>
  );
}

/* ====== STORY PÔLE (1080×1920) ====== */
function StoryPole({ branch }) {
  const b = branch;
  const services = b.services.filter((s) => s !== "Autre").slice(0, 5);
  return (
    <div
      className="relative bg-black text-white overflow-hidden flex flex-col"
      style={{ width: 1080, height: 1920 }}
    >
      <div style={{ height: 60, backgroundColor: b.accent }} />
      <div
        className="absolute inset-0"
        style={{ background: `radial-gradient(60% 35% at 50% 15%, ${b.accent}40, transparent 70%)` }}
      />
      <div className="relative flex-1 flex flex-col items-center text-center px-16 pt-24">
        <div
          className="rounded-lg bg-white flex items-center justify-center"
          style={{ width: 480, height: 480, boxShadow: "0 40px 100px -20px rgba(0,0,0,0.8)" }}
        >
          <img src={LOGOS[b.key]} alt={b.name} style={{ width: "92%", height: "92%", objectFit: "contain" }} />
        </div>

        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 50, fontWeight: 600, color: b.accent, marginTop: 56, lineHeight: 1 }}>
          Equinoxe
        </div>
        <div
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 130,
            fontWeight: 700,
            letterSpacing: "-0.02em",
            color: "#fff",
            lineHeight: 1,
            marginTop: 10,
          }}
        >
          {b.short}
        </div>
        <div style={{ color: b.accent, fontSize: 30, fontWeight: 500, marginTop: 28, maxWidth: 800 }}>{b.tagline}</div>

        <ul style={{ listStyle: "none", padding: 0, margin: "60px 0 0 0", textAlign: "left", maxWidth: 800 }}>
          {services.map((s) => (
            <li key={s} style={{ fontSize: 36, color: "#e5e5e5", lineHeight: 1.25, marginBottom: 22, paddingLeft: 40, position: "relative", fontWeight: 500 }}>
              <span style={{ position: "absolute", left: 0, top: 16, width: 16, height: 16, backgroundColor: b.accent, borderRadius: 3 }} />
              {s}
            </li>
          ))}
        </ul>
      </div>
      <div className="relative px-16 pb-24 text-center">
        <div style={{ fontSize: 22, color: "#9ca3af", letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 600 }}>
          Devis sous 24h
        </div>
        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 76, fontWeight: 700, marginTop: 14 }}>
          05&nbsp;32&nbsp;18&nbsp;90&nbsp;44
        </div>
        <div style={{ fontSize: 24, color: "#fff", letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 16, fontWeight: 600 }}>
          groupeequinoxe.fr
        </div>
      </div>
      <div style={{ height: 36, backgroundColor: b.accent }} />
    </div>
  );
}

/* ===== Card wrapper with download ===== */
function VisualCard({ title, format, ratio, scale, width, height, children }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    if (!ref.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(ref.current, {
        cacheBust: true,
        pixelRatio: 1,
        skipFonts: false,
      });
      const link = document.createElement("a");
      link.download = `${title.toLowerCase().replace(/\s+/g, "-")}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Image téléchargée");
    } catch (e) {
      toast.error("Erreur lors du téléchargement");
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-[#0e0e0e] p-5" data-testid={`social-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">{format}</div>
          <div className="font-display text-lg font-semibold mt-1">{title}</div>
          <div className="text-[10px] text-zinc-600 mt-1">{ratio}</div>
        </div>
        <button
          onClick={download}
          disabled={busy}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white text-black text-xs font-semibold disabled:opacity-50"
          data-testid={`download-${title.toLowerCase().replace(/\s+/g, "-")}`}
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
          PNG
        </button>
      </div>
      <div
        className="overflow-hidden rounded-md bg-black mx-auto"
        style={{ width: width * scale, height: height * scale }}
      >
        <div style={{ width, height, transform: `scale(${scale})`, transformOrigin: "top left" }}>
          <div ref={ref}>{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function SocialMedia() {
  const { user, ready } = useAuth();
  if (ready === false) return null;
  if (ready && !user) return <Navigate to="/admin/login" replace />;

  // Scale factors so the previews fit nicely in the grid
  const POST_SCALE = 0.32; // 1080 * 0.32 ≈ 346px
  const STORY_SCALE = 0.24; // 1080 * 0.24 ≈ 259px wide, 1920 * 0.24 ≈ 460px tall

  return (
    <div className="pt-28 pb-24 min-h-screen" data-testid="page-social-media">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-white mb-6"
          data-testid="social-back-admin"
        >
          <ArrowLeft size={14} /> Retour au tableau de bord
        </Link>

        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Communication</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">
          <ImageIcon className="inline -mt-1 mr-2" size={32} /> Réseaux sociaux
        </h1>
        <p className="mt-3 text-zinc-400 text-sm max-w-2xl">
          Visuels prêts à publier sur Facebook, Instagram et TikTok — au format post (1080 × 1080) et story (1080 × 1920).
          Cliquez sur <strong>PNG</strong> pour télécharger une image en haute définition.
        </p>

        {/* POSTS 1080x1080 */}
        <section className="mt-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 rounded-md flex items-center justify-center bg-white/10 text-xs font-bold">1:1</div>
            <h2 className="font-display text-2xl font-semibold">Posts carrés — Facebook / Instagram feed</h2>
            <span className="text-xs text-zinc-500">1080 × 1080 px</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" style={{ gridAutoRows: "1fr" }}>
            <VisualCard title="Groupe" format="Post" ratio="1080 × 1080 px" scale={POST_SCALE} width={1080} height={1080}>
              <PostGroupe />
            </VisualCard>
            {["studio", "securite", "proprete"].map((k) => (
              <VisualCard key={k} title={BRANCHES[k].short} format="Post" ratio="1080 × 1080 px" scale={POST_SCALE} width={1080} height={1080}>
                <PostPole branch={BRANCHES[k]} />
              </VisualCard>
            ))}
          </div>
        </section>

        {/* STORIES 1080x1920 */}
        <section className="mt-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 rounded-md flex items-center justify-center bg-white/10 text-xs font-bold">9:16</div>
            <h2 className="font-display text-2xl font-semibold">Stories verticales — Instagram / Facebook / TikTok</h2>
            <span className="text-xs text-zinc-500">1080 × 1920 px</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <VisualCard title="Groupe Story" format="Story" ratio="1080 × 1920 px" scale={STORY_SCALE} width={1080} height={1920}>
              <StoryGroupe />
            </VisualCard>
            {["studio", "securite", "proprete"].map((k) => (
              <VisualCard key={k} title={`${BRANCHES[k].short} Story`} format="Story" ratio="1080 × 1920 px" scale={STORY_SCALE} width={1080} height={1920}>
                <StoryPole branch={BRANCHES[k]} />
              </VisualCard>
            ))}
          </div>
        </section>

        {/* Tips */}
        <div className="mt-16 rounded-xl border border-white/10 bg-[#0e0e0e] p-8">
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-3">Conseils publication</div>
          <ul className="text-sm text-zinc-300 space-y-2 list-disc list-inside marker:text-zinc-500">
            <li><strong>Facebook / Instagram (feed)</strong> : utilisez les Posts carrés (1080 × 1080)</li>
            <li><strong>Instagram Reels / TikTok / Stories</strong> : utilisez les Stories verticales (1080 × 1920)</li>
            <li>Téléchargez les PNG, puis publiez-les directement depuis l'application mobile du réseau</li>
            <li>Hashtags suggérés : <code className="text-zinc-400">#GroupeEquinoxe #EquinoxeStudio #EquinoxeSécurité #EquinoxePropreté #Lot #Cahors</code></li>
            <li>Variez les visuels (groupe / pôle) pour ne pas lasser votre audience</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
